import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, DatePicker, Select, Space, Table, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import {
  deductionApi,
  type DeductionBatchListItem,
  type DeductionBatchListQuery,
  type DeductionBatchOutcome,
} from '../../api/deduction.api';
import { orgCampusApi, type CampusItem } from '../../api/campuses.api';
import { classesApi, type ClassListItem } from '../../api/classes.api';
import { staffApi, type StaffListItem } from '../../api/staff.api';
import { studentsApi, type StudentListItem } from '../../api/students.api';
import { EllipsisLink, EllipsisText } from '../../components/EllipsisCell';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { DeductionDetailDrawer, OutcomeTag } from './DeductionDetailDrawer';

const { RangePicker } = DatePicker;
const { Title } = Typography;

const resultOptions: Array<{ value: DeductionBatchOutcome; label: string }> = [
  { value: 'ALL_SUCCESS', label: '全部成功' },
  { value: 'PARTIAL_FAILURE', label: '部分失败' },
  { value: 'ALL_FAILURE', label: '全部失败' },
];

function formatDateTime(value: string): string {
  return dayjs(value).format('YYYY-MM-DD HH:mm');
}

/** ADM-22 消课记录：筛选、分页与批次只读追溯。 */
export function DeductionRecordPage() {
  const [rows, setRows] = useState<DeductionBatchListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<DeductionBatchListQuery>({});
  const [campuses, setCampuses] = useState<CampusItem[]>([]);
  const [classes, setClasses] = useState<ClassListItem[]>([]);
  const [teachers, setTeachers] = useState<StaffListItem[]>([]);
  const [students, setStudents] = useState<StudentListItem[]>([]);
  const [activeBatchId, setActiveBatchId] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      orgCampusApi.listCampuses({ page: 1, pageSize: 100 }),
      classesApi.listClasses({ page: 1, pageSize: 100 }),
      staffApi.listStaffs({ position: 'TEACHER', page: 1, pageSize: 100 }),
      studentsApi.listStudents({ page: 1, pageSize: 100 }),
    ])
      .then(([campusResult, classResult, teacherResult, studentResult]) => {
        setCampuses(campusResult.items);
        setClasses(classResult.items);
        setTeachers(teacherResult.items);
        setStudents(studentResult.items);
      })
      .catch(() => message.error('筛选项加载失败，请刷新重试'));
  }, []);

  const loadBatches = useCallback(async () => {
    setLoading(true);
    try {
      // ADM-22 固定 NORMAL 口径（补消批次归 ADM-33 补消页，R9 互斥）
      const result = await deductionApi.listBatches({
        ...filters,
        batchType: 'NORMAL',
        page,
        pageSize,
      });
      setRows(result.items);
      setTotal(result.total);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '消课记录加载失败');
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    void loadBatches();
  }, [loadBatches]);

  const updateFilters = (patch: Partial<DeductionBatchListQuery>) => {
    setFilters((current) => ({ ...current, ...patch }));
    setPage(1);
  };

  const classOptions = useMemo(
    () => classes.filter((item) => !filters.campusId || item.campusId === filters.campusId),
    [classes, filters.campusId],
  );

  const baseColumns = useMemo<ColumnsType<DeductionBatchListItem>>(
    () => [
      {
        title: '消课批次号',
        dataIndex: 'id',
        width: 220,
        ellipsis: true,
        fixed: 'left',
        render: (value: string) => (
          <EllipsisLink value={value} onClick={() => setActiveBatchId(value)} />
        ),
      },
      {
        title: '课次',
        key: 'session',
        width: 160,
        ellipsis: true,
        render: (_, record) =>
          `${record.sessionDate} ${dayjs(record.startAt).format('HH:mm')}`,
      },
      {
        title: '班级',
        dataIndex: 'className',
        width: 180,
        ellipsis: true,
        render: (value: string) => <EllipsisText value={value} />,
      },
      {
        title: '校区',
        dataIndex: 'campusName',
        width: 120,
        ellipsis: true,
        render: (value: string) => <EllipsisText value={value} empty="—" />,
      },
      { title: '教师', dataIndex: 'teacherName', width: 100 },
      { title: '应消人数', dataIndex: 'expectedCount', width: 95, align: 'center' },
      { title: '成功人数', dataIndex: 'successCount', width: 95, align: 'center' },
      { title: '失败人数', dataIndex: 'failedCount', width: 95, align: 'center' },
      { title: '操作员工', dataIndex: 'operatorName', width: 110 },
      {
        title: '操作时间',
        dataIndex: 'createdAt',
        width: 165,
        render: (value: string) => formatDateTime(value),
      },
      {
        title: '批次结果',
        dataIndex: 'result',
        width: 120,
        fixed: 'right',
        render: (value: DeductionBatchOutcome) => <OutcomeTag result={value} />,
      },
    ],
    [],
  );
  const { columns, components } = useResizableColumns(baseColumns);

  const dateRange: [Dayjs, Dayjs] | null =
    filters.dateFrom && filters.dateTo
      ? [dayjs(filters.dateFrom), dayjs(filters.dateTo)]
      : null;

  return (
    <div>
      <div className="page-header-row">
        <div>
          <Title level={3}>消课记录</Title>
          <p className="page-header-desc">
            查看每个课次的消课批次结果，并追溯学生扣减明细与流水号。
          </p>
        </div>
      </div>
      <Card className="page-filter-card">
        <Space wrap size={12}>
          <Select
            allowClear
            placeholder="校区"
            aria-label="校区"
            style={{ width: 150 }}
            value={filters.campusId}
            options={campuses.map((item) => ({ value: item.id, label: item.name }))}
            onChange={(campusId) =>
              updateFilters({
                campusId,
                classId: classes.some(
                  (item) => item.id === filters.classId && item.campusId === campusId,
                )
                  ? filters.classId
                  : undefined,
              })
            }
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="班级"
            aria-label="班级"
            style={{ width: 170 }}
            value={filters.classId}
            options={classOptions.map((item) => ({ value: item.id, label: item.name }))}
            onChange={(classId) => updateFilters({ classId })}
          />
          <RangePicker
            value={dateRange}
            placeholder={['课次日期开始', '课次日期结束']}
            onChange={(value) =>
              updateFilters({
                dateFrom: value?.[0]?.format('YYYY-MM-DD'),
                dateTo: value?.[1]?.format('YYYY-MM-DD'),
              })
            }
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="教师"
            aria-label="教师"
            style={{ width: 140 }}
            value={filters.teacherId}
            options={teachers.map((item) => ({ value: item.id, label: item.name }))}
            onChange={(teacherId) => updateFilters({ teacherId })}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="学生"
            aria-label="学生"
            style={{ width: 150 }}
            value={filters.studentId}
            options={students.map((item) => ({
              value: item.id,
              label: `${item.name}（${item.studentNo}）`,
            }))}
            onChange={(studentId) => updateFilters({ studentId })}
          />
          <Select
            allowClear
            placeholder="消课结果"
            aria-label="消课结果"
            style={{ width: 140 }}
            value={filters.result}
            options={resultOptions}
            onChange={(result) => updateFilters({ result })}
          />
          <Button
            onClick={() => {
              setFilters({});
              setPage(1);
            }}
          >
            重置
          </Button>
        </Space>
      </Card>
      <Card className="page-content-card">
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          components={components}
          dataSource={rows}
          scroll={{ x: 1520 }}
          data-testid="deduction-record-table"
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (count) => `共 ${count} 条`,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize);
            },
          }}
        />
      </Card>
      {activeBatchId && (
        <DeductionDetailDrawer
          batchId={activeBatchId}
          onClose={() => setActiveBatchId(null)}
        />
      )}
    </div>
  );
}
